import React from 'react';
import { X, Edit, Copy, Link as LinkIcon, Trash2, CalendarRange, FileDown, ImageDown, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';

interface EventActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: any;
  onCopyRegistrationLink: (event: any) => void;
  onCopyShortLink?: (event: any) => void;
  onOpenShareModal?: (event: any) => void;
  onCopyUsherLink: (event: any) => void;
  onDownloadQrPdf: (event: any) => void;
  onDownloadQrPng: (event: any) => void;
  onDuplicate: (event: any) => void;
  onDelete: (event: any) => void;
  onSettings: (event: any) => void;
  onEdit: (event: any) => void;
}

const EventActionsModal = ({
  isOpen,
  onClose,
  event,
  onCopyRegistrationLink,
  onCopyShortLink,
  onOpenShareModal,
  onCopyUsherLink,
  onDownloadQrPdf,
  onDownloadQrPng,
  onDuplicate,
  onDelete,
  onSettings,
  onEdit,
}: EventActionsModalProps) => {
  const navigate = useNavigate();

  if (!isOpen || !event) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm transform transition-all overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              Manage Event
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Action List */}
          <div className="p-2 space-y-1">
            <button
              onClick={() => {
                onEdit(event);
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Edit className="h-4 w-4 text-gray-500" />
              Edit Event
            </button>
            <button
              onClick={() => {
                onSettings(event);
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Settings className="h-4 w-4 text-gray-500" />
              Event Settings
            </button>
            <button
              onClick={() => {
                onDuplicate(event);
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Copy className="h-4 w-4 text-gray-500" />
              Duplicate Event
            </button>
            
            <div className="border-t my-1"></div>

            {onOpenShareModal && (
              <button
                onClick={() => {
                  onOpenShareModal(event);
                  onClose();
                }}
                className="w-full flex items-center justify-between px-4 py-2.5 text-left text-sm text-teal-800 bg-teal-50/70 hover:bg-teal-100/70 rounded-lg transition-colors font-semibold"
              >
                <div className="flex items-center gap-3">
                  <LinkIcon className="h-4 w-4 text-teal-600" />
                  <span>Share Link (Shorten URL)</span>
                </div>
                <span className="text-[10px] bg-teal-200/80 text-teal-900 px-1.5 py-0.5 rounded font-bold">Short URL</span>
              </button>
            )}

            {onCopyShortLink && (
              <button
                onClick={() => {
                  onCopyShortLink(event);
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-teal-700 hover:bg-teal-50 rounded-lg transition-colors font-medium"
              >
                <LinkIcon className="h-4 w-4 text-teal-600" />
                Copy Shortened Link
              </button>
            )}

            <button
              onClick={() => {
                onCopyRegistrationLink(event);
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LinkIcon className="h-4 w-4 text-gray-500" />
              Copy Standard Link
            </button>

            <button
              onClick={() => {
                onCopyUsherLink(event);
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <CalendarRange className="h-4 w-4 text-gray-500" />
              Copy Usher Link
            </button>
            <div className="border-t my-1"></div>
            <button
              onClick={() => {
                onDownloadQrPdf(event);
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FileDown className="h-4 w-4 text-gray-500" />
              Download QR PDF
            </button>
            <button
              onClick={() => {
                onDownloadQrPng(event);
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ImageDown className="h-4 w-4 text-gray-500" />
              Download QR PNG
            </button>
            <div className="border-t my-1"></div>
            <button
              onClick={() => {
                onDelete(event);
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
              Delete Event
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventActionsModal;
